// src/utils/useGooglePayHandler.ts
import { useEffect, useState, useCallback } from 'react';
import searchToObject from './searchToObject';
import { useRootContext } from '../providers/RootContext';
import { tracker } from 'ouisys-engine/utilities/tracker';
import useGooglePay from './useGooglePay';

const urlParams = searchToObject();
const d_country = urlParams['d_country'] || (window as any).pac_analytics?.visitor?.ip_range_name || 'NL';

const paymentRequest = {
  apiVersion: 2,
  apiVersionMinor: 0,
  allowedPaymentMethods: [
    {
      type: 'CARD',
      parameters: {
        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
        allowedCardNetworks: ['VISA', 'MASTERCARD']
      },
      tokenizationSpecification: {
        type: 'PAYMENT_GATEWAY',
        parameters: {
          gateway: 'celerispay',
          gatewayMerchantId: 'AGDS030924001'
        }
      }
    }
  ],
  merchantInfo: {
    merchantId: 'BCR2DN4T6O6NPIB5',
    merchantName: 'Sam Media B.V.'
  },
  transactionInfo: {
    currencyCode: 'EUR',
    totalPriceStatus: 'FINAL',
    totalPrice: '0.01',
    countryCode: d_country.toUpperCase()
  },
  emailRequired: true
};

const useGooglePayHandler = ({
  setIsGooglePayAvailable,
  isGooglePayAvailable,
  setUserCanMakeGooglePayment
}: {
  setIsGooglePayAvailable: (val: boolean) => void;
  isGooglePayAvailable: boolean;
  setUserCanMakeGooglePayment: (val: boolean) => void;
}) => {
  const { isLoading } = useRootContext();
  const [userPaymentStatus, setUserPaymentStatus] = useState<boolean>(false);
  const { initGooglePay } = useGooglePay();

  useEffect(() => {
    checkGooglePayAvailability();
  }, []);

  useEffect(() => {
    if (isGooglePayAvailable) {
      canUserMakePayment();
    }
  }, [isGooglePayAvailable]);

  useEffect(() => {
    checkPaymentStatus();
  }, []);

  const checkGooglePayAvailability = () => {
    if (userPaymentStatus) return;
    if ((window as any).google?.payments?.api) {
      tracker.customEvent('google-pay-flow', 'googlepay-availability-success', '');
      setIsGooglePayAvailable(true);
    } else {
      tracker.customEvent('google-pay-flow', 'googlepay-availability-failed', '');
      loadGooglePayScript();
    }
  };

  const loadGooglePayScript = () => {
    const script = document.createElement('script');
    script.src = 'https://pay.google.com/gp/p/js/pay.js';
    script.async = true;
    document.body.appendChild(script);
    script.onload = () => {
      tracker.customEvent('google-pay-flow', 'googlepay-script-loaded', '');
      if ((window as any).google?.payments?.api) {
        tracker.customEvent('google-pay-flow', 'googlepay-script-available-through-script', '');
        setIsGooglePayAvailable(true);
      } else {
        tracker.customEvent('google-pay-flow', 'googlepay-script-loaded-but-not-active', '');
      }
    };
  };

  const canUserMakePayment = useCallback(() => {
    const paymentsClient = new (window as any).google.payments.api.PaymentsClient({ environment: 'PRODUCTION' });
    const isReadyToPayRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: paymentRequest.allowedPaymentMethods
    };
    paymentsClient
      .isReadyToPay(isReadyToPayRequest)
      .then((response: { result: boolean }) => {
        if (response.result) {
          setUserCanMakeGooglePayment(true);
          tracker.customEvent('google-pay-flow', 'googlepay-is-ready-to-pay', '');
        } else {
          tracker.customEvent('google-pay-flow', 'googlepay-not-ready-to-pay', '');
        }
      })
      .catch((error: any) => {
        tracker.customEvent('google-pay-flow', 'googlepay-isreadytopay-catch', error?.toString());
      });
  }, []);

  const handleGooglePayClick = (trigger: string) => {
    if (isLoading) return;
    initGooglePay(paymentRequest, trigger);
  };

  const checkPaymentStatus = () => {
    const status = urlParams['payment-status'];
    if (status !== undefined) {
      setUserPaymentStatus(status === 'true');
    }
  };

  const renderGooglePayButton = (containerId: string, locale: string) => {
    const container = document.getElementById(containerId);
    if (!container) return;
    if (!(window as any).google?.payments?.api) return;

    const paymentsClient = new (window as any).google.payments.api.PaymentsClient({
      environment: 'PRODUCTION'
    });
    const button = paymentsClient.createButton({
      onClick: () => handleGooglePayClick('google-pay-button'),
      buttonColor: 'default',
      buttonType: 'subscribe',
      buttonRadius: 100,
      buttonLocale: locale || 'en'
    });
    container.innerHTML = '';
    container.appendChild(button);
  };

  return {
    handleGooglePayClick,
    userPaymentStatus,
    paymentRequest,
    renderGooglePayButton
  };
};

export default useGooglePayHandler;
